import { useMemo } from 'react';
import {
  Button,
  CodeBlock,
  DataTable,
  DataTableHeader,
  DataTableHeaderActions,
  DataTableHeaderActionsLeftSection,
  DataTableHeaderActionsRightSection,
  DataTableHeaderRow,
  DataTableHeaderSortMenu,
  DataTableTable,
  Panel,
  PanelInset,
  Text,
  Tooltip,
  Truncated,
} from '@customerio/pluma-components/react';
import { createColumnHelper, type ColumnDef } from '@tanstack/table-core';
import type { Row } from '@tanstack/react-table';
import type { Collection } from '@/services/db/collection/entity';
import type { CollectionItem } from '@/services/db/collection/queries';

export type CollectionItemsTableSorting = {
  id: string;
  desc: boolean;
};

type CollectionItemsTableProps = {
  collection: Collection;
  isLoading?: boolean;
  items: CollectionItem[];
  sorting?: CollectionItemsTableSorting;
  visibleColumns: string[];
  canEditDisplayedColumns: boolean;
  onSortingChange: (sorting: CollectionItemsTableSorting) => void;
  onShowColumnsModal: () => void;
};

const columnHelper = createColumnHelper<CollectionItem>();

function uniqueAttributes(attributes: string[]) {
  return [...new Set(attributes)];
}

function stringifyValue(value: unknown): string {
  if (value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value) ?? '';
  return String(value);
}

type ValueCellProps = {
  value: unknown;
};

function ValueCell({ value }: ValueCellProps) {
  const displayValue = stringifyValue(value);

  return (
    <Tooltip
      as="span"
      placement="top"
      isDisabled={typeof value !== 'object'}
      content={displayValue}
      unsafe_tooltipClassName="fs-mask"
    >
      <Truncated className="fs-mask">{displayValue}</Truncated>
    </Tooltip>
  );
}

type ExpandedRowProps = {
  row: Row<CollectionItem>;
};

function ExpandedRow({ row }: ExpandedRowProps) {
  return <CodeBlock language="json" code={JSON.stringify(row.original, null, 2)} className="fs-mask" />;
}

function createColumnIdMap(schema: string[]): Map<string, string> {
  return new Map(
    schema.map((attribute, index) => [attribute, attribute.trim() !== '' ? attribute : `__empty_key_${index}`]),
  );
}

function createColumns(collection: Collection, columnIdMap: Map<string, string>): ColumnDef<CollectionItem, unknown>[] {
  return collection.schema.map((attribute) =>
    columnHelper.accessor((row) => row[attribute], {
      id: columnIdMap.get(attribute),
      header: attribute,
      enableSorting: attribute.trim() !== '',
      meta: { width: '1fr', minWidth: 160 },
      cell: (info) => <ValueCell value={info.getValue()} />,
    }),
  ) as ColumnDef<CollectionItem, unknown>[];
}

export function CollectionItemsTable({
  collection,
  isLoading,
  items,
  sorting,
  visibleColumns,
  canEditDisplayedColumns,
  onSortingChange,
  onShowColumnsModal,
}: CollectionItemsTableProps) {
  const columnIdMap = useMemo(() => createColumnIdMap(collection.schema), [collection.schema]);
  const schema = useMemo(() => uniqueAttributes(collection.schema), [collection.schema]);
  const visibleSchema = useMemo(() => uniqueAttributes(visibleColumns), [visibleColumns]);
  const tableCollection = useMemo(() => ({ ...collection, schema }), [collection, schema]);
  const columns = useMemo(() => createColumns(tableCollection, columnIdMap), [columnIdMap, tableCollection]);
  const columnOrder = useMemo(
    () => visibleSchema.map((attribute) => columnIdMap.get(attribute) ?? attribute),
    [columnIdMap, visibleSchema],
  );
  const columnVisibility = useMemo(
    () =>
      schema.reduce<Record<string, boolean>>((visibility, attribute) => {
        const id = columnIdMap.get(attribute) ?? attribute;
        visibility[id] = visibleSchema.includes(attribute);
        return visibility;
      }, {}),
    [columnIdMap, schema, visibleSchema],
  );
  const sortingConfig = useMemo(
    () => ({
      value: sorting,
      onChange: onSortingChange,
      isManual: true,
    }),
    [onSortingChange, sorting],
  );

  if (columns.length === 0) {
    return <Text color="subtle">This collection has no attributes to display.</Text>;
  }

  return (
    <DataTable
      isLoading={isLoading}
      caption="A list of entries in the collection."
      data={items}
      columns={columns}
      getRowId={(_row, index) => String(index)}
      withSorting
      sorting={sortingConfig}
      withRowExpanding
      expandedRowComponent={ExpandedRow}
      columnOrder={columnOrder}
      columnVisibility={columnVisibility}
    >
      <Panel>
        <DataTableHeader>
          <DataTableHeaderRow>
            <DataTableHeaderActions>
              <DataTableHeaderActionsLeftSection />
              <DataTableHeaderActionsRightSection>
                <Button variant="subtle" size="sm" isDisabled={!canEditDisplayedColumns} onClick={onShowColumnsModal}>
                  Edit displayed attributes…
                </Button>
                <DataTableHeaderSortMenu />
              </DataTableHeaderActionsRightSection>
            </DataTableHeaderActions>
          </DataTableHeaderRow>
        </DataTableHeader>
        <PanelInset>
          <DataTableTable />
        </PanelInset>
      </Panel>
    </DataTable>
  );
}
